/**
 * "This payment session was already used" - the one error worth not treating as
 * a failure.
 *
 * It happens for a real and benign reason: the card SDK submitted, the payment
 * went through, and something (a retry, a double click, a remount) confirmed
 * the same session again. The payment exists; the second confirm is the thing
 * that failed.
 *
 * What this replaces treated that case as seven substring matches against
 * `JSON.stringify` of the whole event:
 *
 *     errorString.includes("payin config") ||
 *     errorString.includes("unable to process") || …
 *
 * Two of those - `"payin config"` and `"unable to process"` - match a large
 * class of genuine failures, including ones that have nothing to do with
 * reuse. When they matched, the handler `return`ed: no callback, no spinner
 * reset, no telemetry. A patient's card really failing could leave staff
 * watching a spinner with nothing written down anywhere. In two of the
 * checkout providers a match went further and *fabricated* a successful
 * `mock_…` payment.
 *
 * So: match narrowly, and let the caller reconcile against the server rather
 * than infer. `true` here means "ask the API what happened", never "assume it
 * worked".
 */
import { PaymentProcessor } from "@/types/auth";

/**
 * The two literal strings Rainforest sends for a re-submitted payin.
 *
 * Both are full phrases rather than fragments, and both name the payin
 * specifically. Anything vaguer belongs in the decline path, where at least it
 * gets said out loud.
 */
const RAINFOREST_REUSE_MESSAGES = [
  "payin has already been successfully processed",
  "payin config id provided",
] as const;

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const unwrap = (input: unknown): UnknownRecord => {
  let payload: unknown = input;
  // Rainforest's terminal hands over `event.detail` itself, which is already
  // the array - so the `{detail: [...]}` unwrapping below never ran, `isRecord`
  // rejected the array, and every consumed session on that path was reported
  // to staff as a decline for a payment that had gone through.
  if (Array.isArray(payload)) {
    payload = payload[0];
  }
  if (isRecord(payload) && "detail" in payload) {
    const detail = payload.detail;
    const inner = Array.isArray(detail) ? detail[0] : detail;
    // FastAPI's `{detail: "..."}` is the common shape here, so a bare string
    // has to keep its text rather than unwrapping into nothing.
    payload = typeof inner === "string" ? { message: inner } : inner;
  }
  if (isRecord(payload) && isRecord(payload.data)) payload = payload.data;
  return isRecord(payload) ? payload : {};
};

/**
 * Every human-readable string in the payload, lowercased.
 *
 * Bounded to the fields that actually carry messages rather than
 * `JSON.stringify`-ing the object: stringifying means a payin id, a URL or a
 * nested config value can satisfy a substring match by coincidence, which is
 * how `"payin config"` came to match errors that were not about reuse.
 */
const messagesIn = (payload: UnknownRecord): string[] => {
  const keys = ["message", "error", "detail", "description", "reason"];
  const found: string[] = [];
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === "string" && value.trim()) {
      found.push(value.toLowerCase());
    } else if (isRecord(value)) {
      const nested = value.message;
      if (typeof nested === "string" && nested.trim())
        found.push(nested.toLowerCase());
    }
  }
  return found;
};

export function isSessionAlreadyConsumed(
  processor: PaymentProcessor,
  raw: unknown,
): boolean {
  const payload = unwrap(raw);

  if (processor === PaymentProcessor.STRIPE) {
    // Stripe says this structurally, so there is no string matching to do: a
    // re-confirm of an object that is already in its terminal state.
    const error = isRecord(payload.error) ? payload.error : payload;
    const code = error.code;
    const intent = isRecord(error.payment_intent) ? error.payment_intent : null;
    if (
      code === "payment_intent_unexpected_state" &&
      intent?.status === "succeeded"
    ) {
      return true;
    }
    // The SetupIntent equivalent. A remount or a second Save once `inFlight`
    // has reset re-confirms an intent that already captured the card; the card
    // is saved, and reporting it as a decline sends the patient to re-enter a
    // card we are already holding.
    const setupIntent = isRecord(error.setup_intent)
      ? error.setup_intent
      : null;
    return (
      code === "setup_intent_unexpected_state" &&
      setupIntent?.status === "succeeded"
    );
  }

  const messages = messagesIn(payload);
  return messages.some((message) =>
    RAINFOREST_REUSE_MESSAGES.some((reuse) => message.includes(reuse)),
  );
}
